import React, { Fragment, useRef, useState } from "react"

import { useInView } from "react-intersection-observer"

import { Grid } from "@mui/material"
import { Button, InputText, Menu, MenuItem, Skeleton, Typography } from "@synapse-analytics/synapse-ui"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import type { Label } from "dagrejs"

import { ArrowAdornment } from "../../features/Scorecards/components/Condition"

type LabelsMenuProps = {
  filteredLabels: Array<Label>
  value: string
  onChange: (e: InputChangeEvent) => void
  onSelectingLabel: (item: Label) => void
  onAddingNewLabel: (labelName: string) => void
  disabled?: boolean
  placeholder?: string
  onLoadMore?: () => void
  hasMore?: boolean
  isLoading?: boolean
  error?: string | boolean
}

/**
 * Dropdown Menu component for all labels
 */
export function LabelsMenu(props: Readonly<LabelsMenuProps>): React.ReactElement {
  const {
    filteredLabels,
    value,
    onChange,
    onSelectingLabel,
    onAddingNewLabel,
    disabled,
    placeholder = "Label",
    onLoadMore,
    hasMore,
    isLoading,
    error,
  } = props

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const { ref: loadMoreRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently loading
      inView && hasMore && !isLoading && onLoadMore?.()
    },
  })

  const filteredResults = filteredLabels.filter((label) => label.name.toLowerCase().includes(value.toLowerCase()))

  const handleSelectingLabel = (item: Label): void => {
    onSelectingLabel(item)
    setAnchorEl(null)
  }

  const handleAddingNewLabel = (labelName: string): void => {
    onAddingNewLabel(labelName)
    setAnchorEl(null)
  }

  const handleInputChange = (e: InputChangeEvent): void => {
    onChange(e)
    setAnchorEl(inputRef.current)
  }

  return (
    <Fragment>
      <InputText
        fullWidth
        disabled={disabled}
        handleChange={handleInputChange}
        value={value}
        hideDescription
        placeholder={placeholder}
        type="text"
        anchoringRef={inputRef}
        error={error}
        endAdornment={
          filteredLabels?.length > 0 ? (
            <ArrowAdornment anchorEl={anchorEl} onClick={() => setAnchorEl(!!anchorEl ? null : inputRef?.current)} />
          ) : undefined
        }
      />
      <Menu menuMaxContent anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {/* item for Adding new label */}
        {value && !filteredLabels.find((label) => label.name === value) && (
          <MenuItem key={value} value={value} onClick={() => handleAddingNewLabel(value)}>
            <Grid item container flexWrap="nowrap" display="flex" justifyContent="space-between" gap={1}>
              <Typography noWrap variant="a">
                {value}
              </Typography>

              <Button
                size="small"
                onClick={(e) => {
                  e?.stopPropagation()
                  handleAddingNewLabel(value)
                }}
              >
                + Add Label
              </Button>
            </Grid>
          </MenuItem>
        )}

        {filteredResults?.length > 0 &&
          filteredResults?.map((item: Label, index) => (
            <MenuItem
              key={index}
              value={item?.name}
              onClick={() => handleSelectingLabel(item)}
              ref={index === filteredResults.length - 1 && hasMore ? loadMoreRef : undefined}
            >
              <Typography noWrap variant="a">
                {item?.name}
              </Typography>
            </MenuItem>
          ))}

        {isLoading && hasMore && (
          <MenuItem disabled>
            <Skeleton height={24} />
          </MenuItem>
        )}
      </Menu>
    </Fragment>
  )
}
